import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fileUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { ProductCard, SectionTitle } from "../components/shared";
import { Card } from "../components/ui/card";
import { ArrowRight, Truck, ShieldCheck, Sparkles, MapPin, MessageCircle, ShoppingCart, ClipboardList, PackageCheck, FileText, Check } from "lucide-react";
import { Seo } from "../lib/seo";

const HERO = "https://images.pexels.com/photos/3615219/pexels-photo-3615219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function Home() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4)));
    api.get("/products", { params: { sort: "recent" } }).then(({ data }) => setFeatured((f) => f.length ? f : data.slice(0, 4)));
    api.get("/categories").then(({ data }) => setCategories(data.slice(0, 6)));
  }, []);

  const heroImg = fileUrl(settings?.hero_image) || HERO;
  const freeThr = settings?.free_shipping_threshold ?? 60;

  const steps = [
    { icon: ShoppingCart, t: "Scegli il prodotto", d: "Sfoglia il catalogo e apri le schede prodotto." },
    { icon: ClipboardList, t: "Aggiungilo al carrello", d: "Seleziona la quantità e vai al carrello." },
    { icon: ShieldCheck, t: "Completa l'ordine", d: "Checkout come ospite e pagamento sicuro con Stripe." },
    { icon: PackageCheck, t: "Ricevi il tuo acquisto", d: "Spedizione secondo le modalità indicate al checkout." },
  ];
  const reasons = [
    { icon: Sparkles, t: "Catalogo online", d: "Consulta i prodotti Herbalife disponibili, sempre aggiornati." },
    { icon: FileText, t: "Schede dettagliate", d: "Descrizione, formato e informazioni disponibili per ogni prodotto." },
    { icon: ShieldCheck, t: "Checkout online sicuro", d: "Pagamento con carta tramite Stripe, senza registrazione." },
    { icon: MessageCircle, t: "Contatta il distributore", d: "Invia una richiesta per domande su prodotti e ordini." },
  ];

  return (
    <div>
      <Seo
        title="Prodotti Herbalife a Rossano | Herbalife Shop"
        description="Scopri il catalogo di prodotti Herbalife disponibili online a Rossano e Corigliano-Rossano: consulta le schede, aggiungi al carrello e completa l'ordine. Negozio di un distributore indipendente."
        canonical="/"
      />

      <section className="relative overflow-hidden bg-slate-900">
        <img src={heroImg} alt="Prodotti Herbalife" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 px-4 py-1.5 text-sm font-medium text-emerald-100"><MapPin className="w-4 h-4" /> Rossano · Corigliano-Rossano</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight font-display text-balance">Prodotti Herbalife a Rossano</h1>
            <p className="mt-5 text-lg text-slate-200 leading-relaxed">Scopri il catalogo di prodotti Herbalife disponibili online e scegli comodamente ciò che stai cercando. Negozio di un distributore indipendente: consulta le schede, aggiungi al carrello e completa l'ordine online.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-full px-8 py-6 text-base" onClick={() => navigate("/catalogo")} data-testid="hero-catalog-btn">Scopri i prodotti <ArrowRight className="w-5 h-5 ml-1" /></Button>
              <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-base bg-transparent text-white border-white/30 hover:bg-white/10" onClick={() => navigate("/contatta-il-distributore")} data-testid="hero-contact-btn"><MessageCircle className="w-5 h-5 mr-2" /> Contatta il distributore</Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 -mt-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[{ icon: Truck, t: "Spedizione", d: `Gratuita sopra €${freeThr}` },
            { icon: ShieldCheck, t: "Pagamenti sicuri", d: "Checkout protetto con Stripe" },
            { icon: Sparkles, t: "Distributore indipendente", d: "Acquisto online semplice" }].map((c) => (
            <Card key={c.t} className="p-5 rounded-2xl border-slate-200 shadow-lg shadow-emerald-900/5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600"><c.icon className="w-6 h-6" /></div>
              <div><h3 className="font-display font-bold text-slate-900">{c.t}</h3><p className="text-sm text-slate-500">{c.d}</p></div>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pt-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 to-emerald-600 px-8 py-12 sm:px-12 sm:py-14" data-testid="home-promo">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-orange-500/30 blur-2xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl sm:text-4xl font-extrabold font-display text-white leading-tight">Trasforma il tuo benessere con <span className="text-orange-400">Herbalife</span></h2>
            <p className="mt-4 text-emerald-50 leading-relaxed text-base sm:text-lg">Prodotti di qualità scientificamente formulati per supportare i tuoi obiettivi di salute, peso e nutrizione. Che tu voglia perdere peso, guadagnare energia o semplicemente sentirti meglio ogni giorno, abbiamo il prodotto giusto per te.</p>
            <Button size="lg" className="mt-8 rounded-full px-8 py-6 text-base bg-orange-500 hover:bg-orange-600 text-white" onClick={() => navigate("/catalogo")} data-testid="promo-cta-btn">Scopri i Prodotti <ArrowRight className="w-5 h-5 ml-1" /></Button>
          </div>
        </div>
      </section>

      {categories.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-16">
          <SectionTitle eyebrow="Sfoglia" title="Categorie principali" />
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((c) => (
              <Link key={c.id} to={`/catalogo?category=${c.id}`} className="group" data-testid={`home-category-${c.id}`}>
                <Card className="overflow-hidden rounded-2xl border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all h-full">
                  <div className="aspect-[4/3] bg-emerald-50 overflow-hidden">
                    {fileUrl(c.image_url) ? <img src={fileUrl(c.image_url)} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-emerald-100" />}
                  </div>
                  <div className="p-3 text-center"><span className="font-semibold text-slate-800 text-sm group-hover:text-primary">{c.name}</span></div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-end justify-between flex-wrap gap-3"><SectionTitle eyebrow="Catalogo" title="I prodotti in evidenza" /><Link to="/catalogo"><Button variant="outline" className="rounded-full">Vedi tutti</Button></Link></div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">{featured.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 py-16">
        <SectionTitle eyebrow="Semplice" title="Come funziona l'ordine" />
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <Card key={s.t} className="p-5 rounded-2xl border-slate-200 relative" data-testid={`home-step-${i}`}>
              <span className="absolute top-4 right-4 text-3xl font-extrabold text-emerald-100 font-display">{i + 1}</span>
              <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 w-fit"><s.icon className="w-5 h-5" /></div>
              <h3 className="mt-3 font-display font-bold text-slate-900">{s.t}</h3>
              <p className="mt-1 text-sm text-slate-500 leading-relaxed">{s.d}</p>
            </Card>
          ))}
        </div>
        <div className="mt-6"><Link to="/spedizioni" className="inline-flex items-center gap-1.5 text-primary font-medium hover:underline">Dettagli su spedizione e consegna <ArrowRight className="w-4 h-4" /></Link></div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-16">
        <SectionTitle eyebrow="Vantaggi" title="Perché acquistare qui" />
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {reasons.map((r) => (
            <Card key={r.t} className="p-5 rounded-2xl border-slate-200 flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 shrink-0"><r.icon className="w-5 h-5" /></div>
              <div><h3 className="font-display font-bold text-slate-900 flex items-center gap-1"><Check className="w-4 h-4 text-emerald-500" /> {r.t}</h3><p className="mt-1 text-sm text-slate-500 leading-relaxed">{r.d}</p></div>
            </Card>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-20">
        <Card className="rounded-3xl border-emerald-100 bg-emerald-50/60 p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-6" data-testid="home-local-cta">
          <div className="p-4 rounded-2xl bg-white text-emerald-600 shadow-sm self-start"><MapPin className="w-7 h-7" /></div>
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">Herbalife a Rossano e Corigliano-Rossano</h2>
            <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">Scopri come acquistare i prodotti Herbalife nella tua zona e come contattare il distributore indipendente.</p>
          </div>
          <Link to="/herbalife-rossano"><Button size="lg" className="rounded-full px-8" data-testid="home-local-cta-btn">Scopri di più <ArrowRight className="w-5 h-5 ml-1" /></Button></Link>
        </Card>
      </section>
    </div>
  );
}
